import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { all_routes } from "../../router/all_routes";
import { API_URL } from "../../../environment";
import { canViewDiscadora } from "./access";

// Área de TESTE da integração CRM → discadora (03/11/2026). Só a conta BotConversa (9175).
// Fluxo: card entra numa etapa ligada a um mailing → o serviço discadora-crm (vigia a cada 5s)
// inscreve o contato → a discadora liga (quando o mailing está rodando) → resultados voltam aqui.

type Dict = Record<string, any>;

interface Estado {
  credencial: { configurada: boolean; email?: string | null; salva_em?: string | null };
  conexao?: { ok: boolean; quando: string; erro?: string | null; conta?: Dict | null } | null;
  trabalhadores: Record<string, { quando: string; erro?: string | null }>;
  inscricoes: Record<string, number>;
  resultados: number;
  fones_autorizados: string[];
  base_url: string;
}

interface Etapa {
  id_funil_pai: number;
  nome_funil_pai: string;
  id_etapa: number;
  nome_etapa: string;
  cards: number;
  mailing_id?: number | null;
}

interface Mapa {
  id: number;
  nome_funil_pai: string;
  id_etapa: number;
  nome_etapa: string;
  mailing_id: number;
  mailing_nome?: string;
  ja_estavam: number;
  na_etapa_agora: number;
  inscricoes: Record<string, number>;
}

interface Inscricao {
  id: number;
  card_id: number;
  fone: string;
  nome: string;
  nome_etapa: string;
  mailing_id: number;
  referencia: string;
  estado: string;
  erro?: string | null;
  lead_id?: number | null;
  duplicado: number;
  criado_em: string;
  telefone_e164?: string;
  ultimo_resultado?: string | null;
  ultima_tentativa?: number | null;
  tentativas_discadora: number;
  teve_pna: number;
}

interface Resultado {
  id: number;
  mailing_id: number;
  lead_id: number;
  resultado: string;
  tentativa_numero: number;
  primeira_nao_atendida: number;
  encerrada_em?: string;
  card_id?: number;
  nome?: string;
  fone?: string;
  telefone?: string;
  nome_etapa?: string;
}

interface Evento {
  id: number;
  quando: string;
  nivel: string;
  tipo: string;
  msg: string;
}

const ESTADOS: Record<string, { rotulo: string; cor: string; dica: string }> = {
  pendente: { rotulo: "Na fila", cor: "#d97706", dica: "Aguardando envio para a discadora" },
  enviando: { rotulo: "Enviando", cor: "#2563eb", dica: "Envio em andamento" },
  enviado: { rotulo: "Na discadora", cor: "#16a34a", dica: "Inscrito no mailing (a discadora liga quando o mailing estiver rodando)" },
  repetido: { rotulo: "Já estava no mailing", cor: "#6b7280", dica: "O mesmo telefone não entra duas vezes no mesmo mailing" },
  bloqueado: { rotulo: "Fora da lista de teste", cor: "#6b7280", dica: "Só os telefones de teste são inscritos" },
  conflito: { rotulo: "Conflito", cor: "#dc2626", dica: "A discadora já tem esse telefone/referência com outros dados" },
  erro: { rotulo: "Erro", cor: "#dc2626", dica: "A discadora recusou ou não respondeu" },
};

const RESULTADOS: Record<string, { rotulo: string; cor: string }> = {
  atendida: { rotulo: "Atendida", cor: "#16a34a" },
  nao_atendida: { rotulo: "Não atendida", cor: "#d97706" },
  ocupado: { rotulo: "Ocupado", cor: "#d97706" },
  falha: { rotulo: "Falha", cor: "#dc2626" },
  cancelada: { rotulo: "Cancelada", cor: "#6b7280" },
  agente_desistiu: { rotulo: "Atendente desistiu", cor: "#6b7280" },
};

const NIVEL_COR: Record<string, string> = { ok: "#16a34a", info: "#2563eb", aviso: "#d97706", erro: "#dc2626" };

const formatFone = (f?: string | null): string => {
  let d = (f || "").replace(/\D/g, "").replace(/^55(?=\d{10,11}$)/, "");
  // celular guardado sem o 9º dígito (Uazapi): mostra como a discadora vai discar
  if (d.length === 10 && /[6-9]/.test(d[2])) d = `${d.slice(0, 2)}9${d.slice(2)}`;
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return f || "—";
};

const formatData = (iso?: string | null, comSegundos = false): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", ...(comSegundos ? { second: "2-digit" } : {}),
  });
};

const haQuanto = (iso?: string | null): string => {
  if (!iso) return "nunca";
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `há ${s}s`;
  if (s < 3600) return `há ${Math.round(s / 60)} min`;
  return formatData(iso);
};

// Rótulo de um item da discadora (mailing, atendente, número) sem depender do formato exato.
const rotulo = (o: Dict, campos: string[]): string => {
  for (const c of campos) if (o && o[c] !== undefined && o[c] !== null && String(o[c]).trim()) return String(o[c]);
  return `#${o?.id ?? "?"}`;
};

const Discadora = () => {
  const navigate = useNavigate();
  const autorizado = canViewDiscadora();
  const isDark = useSelector((state: any) => state.themeSetting?.dataTheme) === "dark";
  const idGrupo = localStorage.getItem("id_grupo") || "";
  const usuario = localStorage.getItem("user_email") || localStorage.getItem("id_user") || "";

  const [estado, setEstado] = useState<Estado | null>(null);
  const [etapas, setEtapas] = useState<Etapa[]>([]);
  const [mapas, setMapas] = useState<Mapa[]>([]);
  const [inscricoes, setInscricoes] = useState<Inscricao[]>([]);
  const [resultados, setResultados] = useState<Resultado[]>([]);
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [recursos, setRecursos] = useState<{ mailings: Dict[]; atendentes: Dict[]; numeros: Dict[] } | null>(null);
  const [erroRecursos, setErroRecursos] = useState<string | null>(null);
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [aviso, setAviso] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);

  // formulários
  const [trocandoCred, setTrocandoCred] = useState(false);
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [mailingSel, setMailingSel] = useState<string>("");
  const [novoAberto, setNovoAberto] = useState(false);
  const [novo, setNovo] = useState({
    nome: `CRM — teste de integração ${new Date().toLocaleDateString("pt-BR")}`,
    atendente_id: "",
    numero_id: "",
    tentativas_max: 3,
    intervalo: 1,
    horario_ini: "08:00",
    horario_fim: "20:00",
  });
  const [funilSel, setFunilSel] = useState<string>("");
  const [etapaSel, setEtapaSel] = useState<string>("");
  const [mapaMailing, setMapaMailing] = useState<string>("");
  const [foneNovo, setFoneNovo] = useState("");
  const [confirmarInicio, setConfirmarInicio] = useState<Dict | null>(null);
  const [leadsMailing, setLeadsMailing] = useState<Dict[] | null>(null);
  const [registroAberto, setRegistroAberto] = useState(true);
  const carregandoRef = useRef(false);

  useEffect(() => {
    if (!autorizado) navigate(all_routes.chat, { replace: true });
  }, [autorizado, navigate]);

  // ── API ────────────────────────────────────────────────────────────────
  const api = useCallback(
    async (metodo: "GET" | "POST" | "DELETE", caminho: string, corpo?: Dict) => {
      const q = new URLSearchParams({ id_grupo: idGrupo, usuario });
      const resp = await fetch(`${API_URL}/discadora${caminho}${caminho.includes("?") ? "&" : "?"}${q.toString()}`, {
        method: metodo,
        headers: corpo ? { "Content-Type": "application/json" } : undefined,
        body: corpo ? JSON.stringify({ ...corpo, id_grupo: Number(idGrupo), usuario }) : undefined,
      });
      const dados = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(dados?.error || `Falha (HTTP ${resp.status})`);
      return dados;
    },
    [idGrupo, usuario]
  );

  const carregarPainel = useCallback(async () => {
    if (carregandoRef.current) return;
    carregandoRef.current = true;
    try {
      const [e, m, i, r, ev] = await Promise.all([
        api("GET", "/estado"),
        api("GET", "/mapa"),
        api("GET", "/inscricoes?limite=100"),
        api("GET", "/resultados?limite=100"),
        api("GET", "/eventos?limite=60"),
      ]);
      setEstado(e);
      setMapas(m);
      setInscricoes(i);
      setResultados(r);
      setEventos(ev);
      setErroGeral(null);
    } catch (err: any) {
      setErroGeral(err?.message || "Não consegui falar com o serviço da discadora.");
    } finally {
      carregandoRef.current = false;
    }
  }, [api]);

  const carregarRecursos = useCallback(async () => {
    try {
      const r = await api("GET", "/recursos");
      setRecursos(r);
      setErroRecursos(null);
    } catch (err: any) {
      setErroRecursos(err?.message || "Não consegui ler os dados da discadora.");
    }
  }, [api]);

  const carregarEtapas = useCallback(async () => {
    try {
      setEtapas(await api("GET", "/etapas"));
    } catch (err: any) {
      setErroGeral(err?.message || "Não consegui ler as etapas do CRM.");
    }
  }, [api]);

  useEffect(() => {
    if (!autorizado) return;
    carregarPainel();
    carregarEtapas();
    const t = setInterval(carregarPainel, 5000);
    return () => clearInterval(t);
  }, [autorizado, carregarPainel, carregarEtapas]);

  const credOk = !!estado?.credencial?.configurada;
  useEffect(() => {
    if (credOk && !recursos) carregarRecursos();
  }, [credOk, recursos, carregarRecursos]);

  const executar = async (chave: string, fn: () => Promise<any>, sucesso?: string) => {
    setOcupado(chave);
    setAviso(null);
    try {
      await fn();
      if (sucesso) setAviso({ tipo: "ok", texto: sucesso });
      await carregarPainel();
    } catch (err: any) {
      setAviso({ tipo: "erro", texto: err?.message || "Algo deu errado." });
    } finally {
      setOcupado(null);
    }
  };

  // ── derivados ──────────────────────────────────────────────────────────
  const mailings = useMemo(() => recursos?.mailings || [], [recursos]);
  const mailingAtual = useMemo(() => mailings.find((m) => String(m.id) === mailingSel) || null, [mailings, mailingSel]);
  useEffect(() => {
    if (!mailingSel && mailings.length) setMailingSel(String(mailings[mailings.length - 1].id));
  }, [mailings, mailingSel]);

  const funis = useMemo(() => {
    const m = new Map<string, string>();
    etapas.forEach((e) => m.set(String(e.id_funil_pai), e.nome_funil_pai));
    return Array.from(m.entries());
  }, [etapas]);
  useEffect(() => {
    if (!funilSel && funis.length) {
      const teste = funis.find(([, n]) => /discadora/i.test(n));
      setFunilSel((teste || funis[0])[0]);
    }
  }, [funis, funilSel]);
  const etapasDoFunil = useMemo(() => etapas.filter((e) => String(e.id_funil_pai) === funilSel), [etapas, funilSel]);
  // No funil de teste, a etapa "Ligar" já vem escolhida.
  useEffect(() => {
    if (etapaSel) return;
    const ligar = etapasDoFunil.find((e) => /^ligar$/i.test(e.nome_etapa.trim()));
    if (ligar) setEtapaSel(String(ligar.id_etapa));
  }, [etapasDoFunil, etapaSel]);

  const inscritosNoMailing = (mid: number) =>
    inscricoes.filter((i) => i.mailing_id === mid && i.estado === "enviado").length;

  if (!autorizado) return null;

  // ── visual ─────────────────────────────────────────────────────────────
  const bgCard = isDark ? "#1f2c34" : "#ffffff";
  const bgHead = isDark ? "#172229" : "#f8fafc";
  const border = isDark ? "#2a3942" : "#e6e9ef";
  const textMain = isDark ? "#e9edef" : "#1a1a2e";
  const textMuted = isDark ? "#8696a0" : "#6b7280";
  const card: React.CSSProperties = { background: bgCard, border: `1px solid ${border}`, borderRadius: 12, padding: 16 };
  const lbl: React.CSSProperties = { fontSize: 11, color: textMuted, fontWeight: 600, marginBottom: 2, display: "block" };
  const th: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, color: textMuted, textTransform: "uppercase", letterSpacing: 0.3, padding: "9px 10px",
    whiteSpace: "nowrap", background: bgHead, borderBottom: `1px solid ${border}`, position: "sticky", top: 0, zIndex: 1, textAlign: "left",
  };
  const td: React.CSSProperties = { fontSize: 13, color: textMain, padding: "9px 10px", borderBottom: `1px solid ${border}`, verticalAlign: "top" };

  const Badge = ({ cor, children, dica }: { cor: string; children: React.ReactNode; dica?: string }) => (
    <span
      title={dica}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, fontWeight: 700, color: cor,
        background: `${cor}1a`, border: `1px solid ${cor}40`, borderRadius: 999, padding: "2px 9px", whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );

  const Titulo = ({ n, icone, texto, dir }: { n?: number; icone: string; texto: string; dir?: React.ReactNode }) => (
    <div className="d-flex align-items-center justify-content-between mb-3 gap-2 flex-wrap">
      <div className="d-flex align-items-center gap-2" style={{ color: textMain, fontWeight: 700, fontSize: 15 }}>
        {n !== undefined && (
          <span style={{ width: 24, height: 24, borderRadius: 999, background: "#2563eb", color: "#fff", fontSize: 12, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            {n}
          </span>
        )}
        <i className={`ti ti-${icone}`} style={{ color: "#2563eb" }} />
        {texto}
      </div>
      {dir}
    </div>
  );

  const vivo = (nome: string) => {
    const b = estado?.trabalhadores?.[nome];
    const atrasado = !b || Date.now() - new Date(b.quando).getTime() > 30000;
    const cor = !b ? "#6b7280" : b.erro && b.erro !== "sem credencial" ? "#dc2626" : atrasado ? "#d97706" : "#16a34a";
    const txt = !b ? "parado" : b.erro === "sem credencial" ? "aguardando credencial" : b.erro ? "com erro" : haQuanto(b.quando);
    return (
      <Badge cor={cor} dica={b?.erro || undefined}>
        <span style={{ width: 7, height: 7, borderRadius: 99, background: cor, display: "inline-block" }} />
        {nome}: {txt}
      </Badge>
    );
  };

  const conta = estado?.conexao?.conta || null;
  const contaResumo = conta
    ? Object.entries(conta)
        .filter(([, v]) => ["string", "number", "boolean"].includes(typeof v))
        .slice(0, 5)
    : [];

  const cont = estado?.inscricoes || {};

  return (
    <div className="page-wrapper">
      <div className="content">
        <div style={{ width: "100%" }}>
          {/* Cabeçalho */}
          <div className="d-flex align-items-start justify-content-between mb-3 flex-wrap gap-2">
            <div>
              <h4 className="mb-1" style={{ color: textMain }}>
                <i className="ti ti-phone-call me-2" style={{ color: "#2563eb" }} />
                Discadora <span style={{ fontSize: 13, color: "#d97706", fontWeight: 700 }}>· área de teste</span>
              </h4>
              <p className="mb-0" style={{ color: textMuted, fontSize: 13, maxWidth: 760 }}>
                Quando um card entra numa etapa ligada a um mailing, o CRM inscreve o contato na discadora. Ela liga
                enquanto o mailing estiver <b>rodando</b>, e os resultados voltam para cá. Só esta conta vê esta área.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {vivo("vigia")}
              {vivo("envio")}
              {vivo("resultados")}
            </div>
          </div>

          {erroGeral && (
            <div className="mb-3 p-2" style={{ ...card, borderColor: "#dc2626", color: "#dc2626", fontSize: 13 }}>
              <i className="ti ti-alert-triangle me-1" />
              {erroGeral}
            </div>
          )}
          {aviso && (
            <div
              className="mb-3 p-2 d-flex justify-content-between align-items-start gap-2"
              style={{ ...card, borderColor: aviso.tipo === "ok" ? "#16a34a" : "#dc2626", color: aviso.tipo === "ok" ? "#16a34a" : "#dc2626", fontSize: 13 }}
            >
              <span>
                <i className={`ti ti-${aviso.tipo === "ok" ? "circle-check" : "alert-triangle"} me-1`} />
                {aviso.texto}
              </span>
              <button type="button" className="btn btn-sm p-0" style={{ color: textMuted }} onClick={() => setAviso(null)}>
                <i className="ti ti-x" />
              </button>
            </div>
          )}

          {/* Como testar */}
          <div className="mb-3" style={{ ...card, background: isDark ? "#1b2a33" : "#f0f6ff", borderColor: isDark ? "#2a3942" : "#cfe0ff" }}>
            <div style={{ fontWeight: 700, color: textMain, fontSize: 13.5, marginBottom: 6 }}>
              <i className="ti ti-list-check me-1" style={{ color: "#2563eb" }} />
              Roteiro do teste
            </div>
            <ol className="mb-0" style={{ color: textMain, fontSize: 13, paddingLeft: 20, lineHeight: 1.7, listStyle: "decimal" }}>
              <li>Salve a credencial da <b>conta cliente</b> do painel da discadora (passo 1).</li>
              <li>Crie um mailing de teste ou escolha um existente. Ele nasce <b>pausado</b> (passo 2).</li>
              <li>Ligue a etapa <b>"Ligar"</b> do funil <b>"Teste Discadora"</b> ao mailing (passo 3).</li>
              <li>No painel da discadora: abra o fone, registre o ramal e ative <b>"Disponível para discagem automática"</b>.</li>
              <li>Clique em <b>Iniciar discagem</b> e arraste o card do seu número para "Ligar". Quando terminar, <b>pause</b>.</li>
            </ol>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(330px, 1fr))", gap: 16, marginBottom: 16 }}>
            {/* 1. Conexão */}
            <div style={card}>
              <Titulo
                n={1}
                icone="plug-connected"
                texto="Conexão"
                dir={
                  credOk && estado?.conexao ? (
                    estado.conexao.ok ? <Badge cor="#16a34a">conectada</Badge> : <Badge cor="#dc2626" dica={estado.conexao.erro || ""}>falhou</Badge>
                  ) : (
                    <Badge cor="#6b7280">sem credencial</Badge>
                  )
                }
              />
              {credOk && !trocandoCred ? (
                <>
                  <div style={{ fontSize: 13, color: textMain }}>
                    <div><span style={lbl}>Login no painel</span>{estado?.credencial.email}</div>
                    {contaResumo.length > 0 && (
                      <div className="mt-2">
                        <span style={lbl}>Conta na discadora</span>
                        {contaResumo.map(([k, v]) => (
                          <div key={k} style={{ fontSize: 12.5 }}>
                            <span style={{ color: textMuted }}>{k}:</span> {String(v)}
                          </div>
                        ))}
                      </div>
                    )}
                    {estado?.conexao?.erro && !estado.conexao.ok && (
                      <div className="mt-2" style={{ color: "#dc2626", fontSize: 12.5 }}>{estado.conexao.erro}</div>
                    )}
                    <div className="mt-2" style={{ fontSize: 12, color: textMuted }}>
                      Última conferência: {haQuanto(estado?.conexao?.quando)} · {estado?.base_url}
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <button type="button" className="btn btn-sm btn-outline-primary" disabled={!!ocupado}
                      onClick={() => executar("testar", async () => { await api("POST", "/testar", {}); await carregarRecursos(); }, "Conexão conferida.")}>
                      <i className="ti ti-refresh me-1" />{ocupado === "testar" ? "Testando…" : "Testar conexão"}
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setTrocandoCred(true)}>
                      Trocar credencial
                    </button>
                    <button type="button" className="btn btn-sm btn-outline-danger" disabled={!!ocupado}
                      onClick={() => executar("remover", async () => { await api("DELETE", "/credencial"); setRecursos(null); }, "Credencial removida.")}>
                      Remover
                    </button>
                  </div>
                </>
              ) : (
                <form
                  onSubmit={(ev) => {
                    ev.preventDefault();
                    executar("cred", async () => {
                      await api("POST", "/credencial", { email: email.trim(), senha });
                      setSenha("");
                      setTrocandoCred(false);
                      setRecursos(null);
                    }, "Credencial salva e login conferido.");
                  }}
                >
                  <label style={lbl}>E-mail da conta cliente do painel</label>
                  <input className="form-control form-control-sm mb-2" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} required />
                  <label style={lbl}>Senha</label>
                  <input className="form-control form-control-sm mb-2" type="password" autoComplete="new-password" value={senha} onChange={(e) => setSenha(e.target.value)} required />
                  <div style={{ fontSize: 12, color: textMuted }} className="mb-2">
                    <i className="ti ti-lock me-1" />
                    A senha fica só no servidor do CRM e nunca volta para a tela. A senha do ramal não serve aqui.
                  </div>
                  <div className="d-flex gap-2">
                    <button type="submit" className="btn btn-sm btn-primary" disabled={!!ocupado}>
                      {ocupado === "cred" ? "Conferindo…" : "Salvar e testar"}
                    </button>
                    {credOk && (
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setTrocandoCred(false)}>
                        Cancelar
                      </button>
                    )}
                  </div>
                </form>
              )}
            </div>

            {/* 2. Mailing */}
            <div style={card}>
              <Titulo
                n={2}
                icone="list-numbers"
                texto="Mailing"
                dir={
                  credOk && (
                    <button type="button" className="btn btn-sm btn-link p-0" onClick={carregarRecursos} title="Recarregar da discadora">
                      <i className="ti ti-refresh" />
                    </button>
                  )
                }
              />
              {!credOk ? (
                <div style={{ fontSize: 13, color: textMuted }}>Salve a credencial primeiro.</div>
              ) : erroRecursos ? (
                <div style={{ fontSize: 13, color: "#dc2626" }}>{erroRecursos}</div>
              ) : !recursos ? (
                <div style={{ fontSize: 13, color: textMuted }}>Carregando da discadora…</div>
              ) : (
                <>
                  {mailings.length ? (
                    <>
                      <label style={lbl}>Mailing</label>
                      <select className="form-select form-select-sm mb-2" value={mailingSel} onChange={(e) => { setMailingSel(e.target.value); setLeadsMailing(null); }}>
                        {mailings.map((m) => (
                          <option key={m.id} value={m.id}>
                            #{m.id} {rotulo(m, ["nome"])} {m.status ? `— ${m.status}` : ""}
                          </option>
                        ))}
                      </select>
                      {mailingAtual && (
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                          <Badge cor={mailingAtual.status === "rodando" ? "#16a34a" : mailingAtual.status === "pausado" ? "#d97706" : "#6b7280"}>
                            {mailingAtual.status || "status ?"}
                          </Badge>
                          {mailingAtual.tentativas_max && (
                            <span style={{ fontSize: 12, color: textMuted }}>
                              {mailingAtual.tentativas_max} tentativa(s)
                              {mailingAtual.horario_ini ? ` · ${mailingAtual.horario_ini}–${mailingAtual.horario_fim}` : ""}
                            </span>
                          )}
                          <span style={{ fontSize: 12, color: textMuted }}>· {inscritosNoMailing(Number(mailingAtual.id))} inscrito(s) por aqui</span>
                        </div>
                      )}
                      <div className="d-flex flex-wrap gap-2">
                        <button type="button" className="btn btn-sm btn-success" disabled={!mailingAtual || !!ocupado}
                          onClick={() => setConfirmarInicio(mailingAtual)}>
                          <i className="ti ti-player-play me-1" />Iniciar discagem
                        </button>
                        <button type="button" className="btn btn-sm btn-warning" disabled={!mailingAtual || !!ocupado}
                          onClick={() => executar("pausar", async () => { await api("POST", `/mailings/${mailingAtual!.id}/status`, { status: "pausado" }); await carregarRecursos(); }, `Mailing #${mailingAtual!.id} pausado.`)}>
                          <i className="ti ti-player-pause me-1" />Pausar
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!mailingAtual}
                          onClick={async () => {
                            try { setLeadsMailing(await api("GET", `/mailings/${mailingAtual!.id}/leads`)); }
                            catch (err: any) { setAviso({ tipo: "erro", texto: err?.message }); }
                          }}>
                          Ver fila
                        </button>
                      </div>
                      {leadsMailing && (
                        <div className="mt-2" style={{ fontSize: 12.5, color: textMain, maxHeight: 140, overflow: "auto", border: `1px solid ${border}`, borderRadius: 8, padding: 8 }}>
                          {!leadsMailing.length ? (
                            <span style={{ color: textMuted }}>Nenhum lead neste mailing.</span>
                          ) : (
                            leadsMailing.map((l) => (
                              <div key={l.id}>
                                #{l.id} {rotulo(l, ["nome"])} · {formatFone(l.telefone_e164)} {l.status ? `· ${l.status}` : ""}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ fontSize: 13, color: textMuted }} className="mb-2">Nenhum mailing nesta conta ainda.</div>
                  )}

                  <button type="button" className="btn btn-sm btn-link px-0 mt-2" onClick={() => setNovoAberto(!novoAberto)}>
                    <i className={`ti ti-chevron-${novoAberto ? "up" : "down"} me-1`} />Novo mailing de teste
                  </button>
                  {novoAberto && (
                    <form
                      className="mt-1"
                      onSubmit={(ev) => {
                        ev.preventDefault();
                        executar("criar", async () => {
                          const n = Math.max(1, Math.min(20, Number(novo.tentativas_max) || 1));
                          const r = await api("POST", "/mailings", {
                            nome: novo.nome,
                            atendente_id: Number(novo.atendente_id),
                            numero_id: Number(novo.numero_id),
                            tentativas_max: n,
                            intervalos_min: Array(n - 1).fill(Math.max(1, Number(novo.intervalo) || 1)),
                            horario_ini: novo.horario_ini,
                            horario_fim: novo.horario_fim,
                            fuso: "America/Sao_Paulo",
                          });
                          await carregarRecursos();
                          if (r?.mailing?.id) setMailingSel(String(r.mailing.id));
                          setNovoAberto(false);
                        }, "Mailing criado (pausado).");
                      }}
                    >
                      <label style={lbl}>Nome</label>
                      <input className="form-control form-control-sm mb-2" value={novo.nome} onChange={(e) => setNovo({ ...novo, nome: e.target.value })} required maxLength={200} />
                      <div className="d-flex gap-2">
                        <div style={{ flex: 1 }}>
                          <label style={lbl}>Atendente</label>
                          <select className="form-select form-select-sm mb-2" value={novo.atendente_id} onChange={(e) => setNovo({ ...novo, atendente_id: e.target.value })} required>
                            <option value="">Escolha…</option>
                            {(recursos.atendentes || []).map((a) => (
                              <option key={a.id} value={a.id}>
                                {rotulo(a, ["nome", "apelido", "ramal", "email"])}{a.ramal ? ` (ramal ${a.ramal})` : ""}{a.ativo === false ? " — inativo" : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={lbl}>Número de saída</label>
                          <select className="form-select form-select-sm mb-2" value={novo.numero_id} onChange={(e) => setNovo({ ...novo, numero_id: e.target.value })} required>
                            <option value="">Escolha…</option>
                            {(recursos.numeros || []).map((n) => (
                              <option key={n.id} value={n.id}>
                                {rotulo(n, ["numero", "nome", "descricao"])}{n.ativo === false ? " — inativo" : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <div style={{ flex: 1 }}>
                          <label style={lbl}>Tentativas</label>
                          <input type="number" min={1} max={20} className="form-control form-control-sm mb-2" value={novo.tentativas_max}
                            onChange={(e) => setNovo({ ...novo, tentativas_max: Number(e.target.value) })} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={lbl}>Intervalo (min)</label>
                          <input type="number" min={1} className="form-control form-control-sm mb-2" value={novo.intervalo}
                            onChange={(e) => setNovo({ ...novo, intervalo: Number(e.target.value) })} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={lbl}>Das</label>
                          <input type="time" className="form-control form-control-sm mb-2" value={novo.horario_ini} onChange={(e) => setNovo({ ...novo, horario_ini: e.target.value })} />
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={lbl}>Até</label>
                          <input type="time" className="form-control form-control-sm mb-2" value={novo.horario_fim} onChange={(e) => setNovo({ ...novo, horario_fim: e.target.value })} />
                        </div>
                      </div>
                      <div style={{ fontSize: 12, color: textMuted }} className="mb-2">
                        Até {novo.tentativas_max} ligação(ões) por contato, com pelo menos {novo.intervalo} min entre elas, das {novo.horario_ini} às {novo.horario_fim} (Brasília).
                        Nasce pausado. A política não muda depois: outra política pede outro mailing.
                      </div>
                      <button type="submit" className="btn btn-sm btn-primary" disabled={!!ocupado}>
                        {ocupado === "criar" ? "Criando…" : "Criar mailing (pausado)"}
                      </button>
                    </form>
                  )}
                </>
              )}
            </div>

            {/* 3. Etapas */}
            <div style={card}>
              <Titulo n={3} icone="git-branch" texto="Etapas que ligam" />
              <div className="d-flex gap-2 flex-wrap">
                <div style={{ flex: "1 1 140px" }}>
                  <label style={lbl}>Funil</label>
                  <select className="form-select form-select-sm mb-2" value={funilSel} onChange={(e) => { setFunilSel(e.target.value); setEtapaSel(""); }}>
                    {funis.map(([id, nome]) => (
                      <option key={id} value={id}>{nome}</option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: "1 1 140px" }}>
                  <label style={lbl}>Etapa</label>
                  <select className="form-select form-select-sm mb-2" value={etapaSel} onChange={(e) => setEtapaSel(e.target.value)}>
                    <option value="">Escolha…</option>
                    {etapasDoFunil.map((e) => (
                      <option key={e.id_etapa} value={e.id_etapa}>
                        {e.nome_etapa} ({e.cards} card{e.cards === 1 ? "" : "s"}){e.mailing_id ? ` → #${e.mailing_id}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: "1 1 140px" }}>
                  <label style={lbl}>Mailing</label>
                  <select className="form-select form-select-sm mb-2" value={mapaMailing} onChange={(e) => setMapaMailing(e.target.value)} disabled={!mailings.length}>
                    <option value="">{mailings.length ? "Escolha…" : "Crie um mailing"}</option>
                    {mailings.map((m) => (
                      <option key={m.id} value={m.id}>#{m.id} {rotulo(m, ["nome"])}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button type="button" className="btn btn-sm btn-primary" disabled={!etapaSel || !mapaMailing || !!ocupado}
                onClick={() => executar("mapa", async () => {
                  await api("POST", "/mapa", { id_etapa: Number(etapaSel), mailing_id: Number(mapaMailing) });
                  await carregarEtapas();
                }, "Etapa ligada. Quem já estava nela não é inscrito: só quem entrar a partir de agora.")}>
                <i className="ti ti-link me-1" />Ligar etapa ao mailing
              </button>

              <div className="mt-3">
                {!mapas.length ? (
                  <div style={{ fontSize: 13, color: textMuted }}>Nenhuma etapa ligada ainda.</div>
                ) : (
                  mapas.map((m) => (
                    <div key={m.id} className="d-flex justify-content-between align-items-start gap-2 mb-2 p-2" style={{ border: `1px solid ${border}`, borderRadius: 8 }}>
                      <div style={{ fontSize: 13, color: textMain }}>
                        <div style={{ fontWeight: 600 }}>
                          {m.nome_funil_pai} › {m.nome_etapa} <i className="ti ti-arrow-right mx-1" style={{ color: textMuted }} /> mailing #{m.mailing_id}
                          {m.mailing_nome ? ` ${m.mailing_nome}` : ""}
                        </div>
                        <div style={{ fontSize: 12, color: textMuted }}>
                          {m.na_etapa_agora} na etapa agora · {m.ja_estavam} já estavam (não ligam)
                          {Object.keys(m.inscricoes || {}).length > 0 &&
                            " · " + Object.entries(m.inscricoes).map(([k, v]) => `${ESTADOS[k]?.rotulo || k}: ${v}`).join(", ")}
                        </div>
                      </div>
                      <button type="button" className="btn btn-sm btn-outline-danger" disabled={!!ocupado} title="Desligar esta etapa da discadora"
                        onClick={() => executar("desligar", async () => { await api("DELETE", `/mapa/${m.id}`); await carregarEtapas(); }, "Etapa desligada.")}>
                        <i className="ti ti-unlink" />
                      </button>
                    </div>
                  ))
                )}
              </div>

              {/* Trava de teste */}
              <div className="mt-3 pt-3" style={{ borderTop: `1px dashed ${border}` }}>
                <span style={lbl}>
                  <i className="ti ti-shield-lock me-1" />
                  Telefones de teste (só eles são inscritos)
                </span>
                <div className="d-flex flex-wrap gap-2 my-2">
                  {(estado?.fones_autorizados || []).map((f) => (
                    <Badge key={f} cor="#2563eb">
                      {formatFone(f)}
                      <button type="button" className="btn p-0 ms-1" style={{ lineHeight: 1, color: "#2563eb", fontSize: 12 }} title="Tirar da lista"
                        onClick={() => executar("fones", () => api("POST", "/fones", { fones: (estado?.fones_autorizados || []).filter((x) => x !== f) }))}>
                        <i className="ti ti-x" />
                      </button>
                    </Badge>
                  ))}
                  {estado && !estado.fones_autorizados.length && (
                    <Badge cor="#dc2626">lista vazia: QUALQUER card que entrar na etapa será inscrito</Badge>
                  )}
                </div>
                <form className="d-flex gap-2"
                  onSubmit={(ev) => {
                    ev.preventDefault();
                    executar("fones", async () => {
                      await api("POST", "/fones", { fones: [...(estado?.fones_autorizados || []), foneNovo] });
                      setFoneNovo("");
                    });
                  }}>
                  <input className="form-control form-control-sm" placeholder="(41) 99999-9999" value={foneNovo} onChange={(e) => setFoneNovo(e.target.value)} />
                  <button type="submit" className="btn btn-sm btn-outline-primary" disabled={!foneNovo.trim() || !!ocupado}>Adicionar</button>
                </form>
              </div>
            </div>
          </div>

          {/* 4. Inscrições */}
          <div style={{ ...card, padding: 0 }} className="mb-3">
            <div style={{ padding: "14px 16px 0" }}>
              <Titulo
                n={4}
                icone="users"
                texto="Inscrições"
                dir={
                  <div className="d-flex flex-wrap gap-1">
                    {Object.entries(cont).map(([k, v]) => (
                      <Badge key={k} cor={ESTADOS[k]?.cor || "#6b7280"}>{ESTADOS[k]?.rotulo || k}: {v}</Badge>
                    ))}
                  </div>
                }
              />
            </div>
            <div style={{ overflow: "auto", maxHeight: 360 }}>
              {!inscricoes.length ? (
                <div className="p-4 text-center" style={{ color: textMuted, fontSize: 13 }}>
                  Ninguém entrou numa etapa ligada ainda. Arraste um card de teste para a etapa e aguarde até 5 segundos.
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                  <thead>
                    <tr>
                      <th style={th}>Entrou</th>
                      <th style={th}>Contato</th>
                      <th style={th}>Etapa</th>
                      <th style={th}>Mailing / lead</th>
                      <th style={th}>Situação</th>
                      <th style={th}>Discadora</th>
                      <th style={th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {inscricoes.map((i) => {
                      const st = ESTADOS[i.estado] || { rotulo: i.estado, cor: "#6b7280", dica: "" };
                      const res = i.ultimo_resultado ? RESULTADOS[i.ultimo_resultado] || { rotulo: i.ultimo_resultado, cor: "#6b7280" } : null;
                      return (
                        <tr key={i.id}>
                          <td style={{ ...td, whiteSpace: "nowrap" }}>{formatData(i.criado_em, true)}</td>
                          <td style={td}>
                            <div style={{ fontWeight: 600 }}>{i.nome}</div>
                            <div style={{ fontSize: 12, color: textMuted, whiteSpace: "nowrap" }}>{formatFone(i.telefone_e164 || i.fone)} · card {i.card_id}</div>
                          </td>
                          <td style={td}>{i.nome_etapa}</td>
                          <td style={{ ...td, whiteSpace: "nowrap" }}>
                            #{i.mailing_id}
                            {i.lead_id ? <span style={{ color: textMuted }}> · lead {i.lead_id}</span> : null}
                            {i.duplicado ? <div style={{ fontSize: 11, color: textMuted }}>repetição idêntica</div> : null}
                          </td>
                          <td style={{ ...td, maxWidth: 280 }}>
                            <Badge cor={st.cor} dica={st.dica}>{st.rotulo}</Badge>
                            {i.erro && <div style={{ fontSize: 11.5, color: textMuted, marginTop: 3 }}>{i.erro}</div>}
                          </td>
                          <td style={td}>
                            {res ? (
                              <>
                                <Badge cor={res.cor}>{res.rotulo}</Badge>
                                <div style={{ fontSize: 11.5, color: textMuted, marginTop: 3 }}>
                                  {i.tentativas_discadora} tentativa(s){i.teve_pna ? " · teve 1º não atendimento" : ""}
                                </div>
                              </>
                            ) : (
                              <span style={{ fontSize: 12, color: textMuted }}>{i.estado === "enviado" ? "aguardando ligação" : "—"}</span>
                            )}
                          </td>
                          <td style={{ ...td, whiteSpace: "nowrap" }}>
                            {i.estado === "erro" && (
                              <button type="button" className="btn btn-sm btn-outline-primary" disabled={!!ocupado} title="Reenviar com a mesma referência"
                                onClick={() => executar("reenviar", () => api("POST", `/inscricoes/${i.id}/reenviar`, {}), "Recolocada na fila.")}>
                                <i className="ti ti-send" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* 5. Resultados */}
          <div style={{ ...card, padding: 0 }} className="mb-3">
            <div style={{ padding: "14px 16px 0" }}>
              <Titulo n={5} icone="phone-check" texto="Resultados das tentativas" dir={<span style={{ fontSize: 12, color: textMuted }}>consulta a discadora a cada 5s</span>} />
            </div>
            <div style={{ overflow: "auto", maxHeight: 320 }}>
              {!resultados.length ? (
                <div className="p-4 text-center" style={{ color: textMuted, fontSize: 13 }}>
                  Nenhuma tentativa encerrada ainda. Cada resultado aparece quando a ligação termina.
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                  <thead>
                    <tr>
                      <th style={th}>Encerrada</th>
                      <th style={th}>Contato</th>
                      <th style={th}>Tentativa</th>
                      <th style={th}>Resultado</th>
                      <th style={th}>Mailing / lead</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultados.map((r) => {
                      const res = RESULTADOS[r.resultado] || { rotulo: r.resultado, cor: "#6b7280" };
                      return (
                        <tr key={r.id}>
                          <td style={{ ...td, whiteSpace: "nowrap" }}>{formatData(r.encerrada_em, true)}</td>
                          <td style={td}>
                            <div style={{ fontWeight: 600 }}>{r.nome || "—"}</div>
                            <div style={{ fontSize: 12, color: textMuted }}>{formatFone(r.telefone || r.fone)}{r.card_id ? ` · card ${r.card_id}` : ""}</div>
                          </td>
                          <td style={td}>{r.tentativa_numero === 0 ? "ainda não discado" : `${r.tentativa_numero}ª`}</td>
                          <td style={td}>
                            <Badge cor={res.cor}>{res.rotulo}</Badge>
                            {!!r.primeira_nao_atendida && <div style={{ fontSize: 11.5, color: "#d97706", marginTop: 3 }}>1º não atendimento</div>}
                          </td>
                          <td style={{ ...td, whiteSpace: "nowrap" }}>#{r.mailing_id} · lead {r.lead_id}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Registro */}
          <div style={card} className="mb-3">
            <Titulo
              icone="history"
              texto="Registro"
              dir={
                <button type="button" className="btn btn-sm btn-link p-0" onClick={() => setRegistroAberto(!registroAberto)}>
                  {registroAberto ? "Esconder" : "Mostrar"}
                </button>
              }
            />
            {registroAberto && (
              <div style={{ maxHeight: 260, overflow: "auto", fontSize: 12.5 }}>
                {!eventos.length ? (
                  <div style={{ color: textMuted }}>Nada registrado ainda.</div>
                ) : (
                  eventos.map((e) => (
                    <div key={e.id} className="d-flex gap-2 py-1" style={{ borderBottom: `1px solid ${border}` }}>
                      <span style={{ color: textMuted, whiteSpace: "nowrap" }}>{formatData(e.quando, true)}</span>
                      <span style={{ width: 8, height: 8, borderRadius: 99, background: NIVEL_COR[e.nivel] || "#6b7280", marginTop: 6, flex: "0 0 8px" }} />
                      <span style={{ color: textMain }}>{e.msg}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Confirmação de início */}
      {confirmarInicio && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
          onClick={() => setConfirmarInicio(null)}
        >
          <div style={{ ...card, maxWidth: 440, width: "100%", boxShadow: "0 20px 50px rgba(0,0,0,.3)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 700, fontSize: 16, color: textMain }} className="mb-2">
              <i className="ti ti-alert-triangle me-2" style={{ color: "#d97706" }} />
              Iniciar discagem?
            </div>
            <p style={{ fontSize: 13.5, color: textMain }}>
              O mailing <b>#{confirmarInicio.id} {rotulo(confirmarInicio, ["nome"])}</b> vai fazer <b>chamadas reais</b> para os contatos
              da lista ({inscritosNoMailing(Number(confirmarInicio.id))} inscrito(s) por esta área) e para quem entrar depois.
            </p>
            <p style={{ fontSize: 12.5, color: textMuted }}>
              Antes: o ramal do atendente precisa estar registrado e "Disponível para discagem automática" no painel. Para parar, use Pausar.
            </p>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setConfirmarInicio(null)}>Cancelar</button>
              <button type="button" className="btn btn-sm btn-success" disabled={!!ocupado}
                onClick={() => {
                  const m = confirmarInicio;
                  setConfirmarInicio(null);
                  executar("iniciar", async () => {
                    await api("POST", `/mailings/${m.id}/status`, { status: "rodando", confirmo: true });
                    await carregarRecursos();
                  }, `Mailing #${m.id} rodando. Pause quando terminar o teste.`);
                }}>
                Sim, iniciar discagem
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Discadora;
